import React, { useMemo, useState } from "react";
import type { Taxonomie } from "../API/Models";

type Props = {
	label?: string;
	placeholder?: string;
	helper?: string;
	options: Taxonomie[];
	value: string[];
	onChange: (next: string[]) => void;
	disabled?: boolean;
	maxVisibleOptions?: number;
};

const DEFAULT_MAX_VISIBLE = 80;

const normalize = (value: string) => value.trim().toLowerCase();

/** Multi-select input for choosing taxonomies with search filtering and chip-style selected items. */
export const TaxonomyMultiSelect: React.FC<Props> = ({
	label,
	placeholder = "Filter taxonomies",
	helper,
	options,
	value,
	onChange,
	disabled = false,
	maxVisibleOptions = DEFAULT_MAX_VISIBLE
}) => {
	const [query, setQuery] = useState("");
	const [isFocused, setIsFocused] = useState(false);

	const selectedIds = useMemo(() => new Set(value), [value]);
	const sortedOptions = useMemo(() => {
		return options
			.filter((option) => option.id)
			.slice()
			.sort((a, b) => {
				const left = (a.title ?? a.id ?? "").toLowerCase();
				const right = (b.title ?? b.id ?? "").toLowerCase();
				return left.localeCompare(right);
			});
	}, [options]);

	const filteredOptions = useMemo(() => {
		const normalized = normalize(query);
		const available = sortedOptions;
		if (!normalized) return available;
		return available.filter((option) => {
			const title = normalize(option.title ?? "");
			const id = normalize(option.id ?? "");
			return title.includes(normalized) || id.includes(normalized);
		});
	}, [sortedOptions, query]);

	const visibleOptions = useMemo(
		() => filteredOptions.slice(0, maxVisibleOptions),
		[filteredOptions, maxVisibleOptions]
	);

	const handleToggle = (id: string) => {
		if (disabled) return;
		if (selectedIds.has(id)) {
			onChange(value.filter((item) => item !== id));
		} else {
			onChange([...value, id]);
		}
	};

	const handleClear = () => {
		if (disabled) return;
		onChange([]);
	};

	const selectedOptions = useMemo(() => {
		if (value.length === 0) return [];
		const map = new Map(sortedOptions.map((opt) => [opt.id!, opt]));
		return value.map((id) => map.get(id)).filter(Boolean) as Taxonomie[];
	}, [sortedOptions, value]);

	const totalCount = sortedOptions.length;
	const filteredCount = filteredOptions.length;
	const showList = isFocused || query.trim().length > 0;

	return (
		<div
			className="space-y-2"
			onFocusCapture={() => setIsFocused(true)}
			onBlurCapture={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
					setIsFocused(false);
				}
			}}
		>
			<div className="flex items-center justify-between gap-2">
				{label && <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>}
				<div className="flex items-center gap-2">
					{value.length > 0 && (
						<button
							type="button"
							onClick={handleClear}
							disabled={disabled}
							className="text-xs font-semibold text-slate-500 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
						>
							Clear
						</button>
					)}
				</div>
			</div>
			<div className="relative">
				<input
					type="text"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					placeholder={placeholder}
					disabled={disabled}
					className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:bg-slate-100"
				/>
				{showList && (
					<div className="absolute left-0 right-0 z-50 mt-2 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
						{visibleOptions.length === 0 ? (
							<p className="text-xs text-slate-500 px-2 py-1">No taxonomies match your search.</p>
						) : (
							<ul className="space-y-1">
								{visibleOptions.map((option) => {
									const id = option.id!;
									const isSelected = selectedIds.has(id);
									return (
										<li key={id}>
											<button
												type="button"
												onClick={() => handleToggle(id)}
												disabled={disabled}
												className={`flex w-full items-center justify-between rounded-md px-2 py-1 text-left text-xs transition ${
													isSelected ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"
												}`}
											>
												<span className="truncate">{option.title || id}</span>
												<span className="ml-3 text-[10px] opacity-70">{id}</span>
											</button>
										</li>
									);
								})}
							</ul>
						)}
					</div>
				)}
			</div>
			{selectedOptions.length > 0 && (
				<div className="flex flex-wrap gap-2">
					{selectedOptions.map((option) => (
						<span
							key={option.id}
							className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700"
						>
							{option.title || option.id}
							<button
								type="button"
								onClick={() => option.id && handleToggle(option.id)}
								disabled={disabled}
								className="text-slate-500 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
								aria-label={`Remove ${option.title ?? option.id}`}
							>
								×
							</button>
						</span>
					))}
				</div>
			)}
			{totalCount > 0 && (
				<div className="sr-only">
					{filteredCount} match{filteredCount === 1 ? "" : "es"} · {totalCount} total
				</div>
			)}
			{filteredOptions.length > maxVisibleOptions && (
				<p className="text-[11px] text-slate-400">
					Showing first {maxVisibleOptions} results. Refine your search to see more.
				</p>
			)}
			{helper && <p className="text-[11px] text-slate-500">{helper}</p>}
		</div>
	);
};

export default TaxonomyMultiSelect;

